import {
  AP_PERMISSIONS,
  PERIOD_TYPES,
  PLAN_STAGES,
  PLAN_VERSION_STATES,
  Plan,
  TimePeriodData,
} from '@shared/schema';

import { getCurrentUtcDateValue, hasActivePeriod } from './periodUtils';

export interface AnnualPlanningPeriodState {
  definitionPeriodActive: boolean;
  definitionActionPeriodActive: boolean;
  selfAssessmentTypePeriodActive: boolean;
  selfAssessmentActionPeriodActive: boolean;
  anyPlanningPeriodActive: boolean;
  canReopenDefinitionNow: boolean;
  canReopenSelfAssessmentNow: boolean;
}

export interface ApprovedOutOfTimeRequestLike {
  id?: number | null;
  status?: string | null;
  usedAt?: string | null;
}

export interface CreatePlanAvailability {
  showCreatePlanButton: boolean;
  canCreateDelayedPlanAsAdmin: boolean;
  canCreateDelayedPlanWithApproval: boolean;
  shouldShowRequestButton: boolean;
  hasApprovedOutOfTimeRequest: boolean;
  isAdmin: boolean;
  isSelfAssessment: boolean;
}

export interface PlanActionAvailability {
  canEditDefinition: boolean;
  canValidateDefinition: boolean;
  canHandleDefinition: boolean;
  canStartSelfAssessment: boolean;
  canEditSelfAssessment: boolean;
  canValidateSelfAssessment: boolean;
  canHandleSelfAssessment: boolean;
  canReopenDefinitionStage: boolean;
  canReopenSelfStage: boolean;
  canShowReopen: boolean;
  canAnnulPlan: boolean;
  canReturnToDefinition: boolean;
}

const getDisabledPlanActionAvailability = (): PlanActionAvailability => ({
  canEditDefinition: false,
  canValidateDefinition: false,
  canHandleDefinition: false,
  canStartSelfAssessment: false,
  canEditSelfAssessment: false,
  canValidateSelfAssessment: false,
  canHandleSelfAssessment: false,
  canReopenDefinitionStage: false,
  canReopenSelfStage: false,
  canShowReopen: false,
  canAnnulPlan: false,
  canReturnToDefinition: false,
});

export const getAnnualPlanningPeriodState = (
  periods: TimePeriodData[],
  currentUtcDateValue = getCurrentUtcDateValue(),
): AnnualPlanningPeriodState => {
  const definitionPeriodActive = hasActivePeriod(
    periods,
    [PERIOD_TYPES.DEFINITION],
    currentUtcDateValue,
  );
  const definitionActionPeriodActive = hasActivePeriod(
    periods,
    [PERIOD_TYPES.DEFINITION, PERIOD_TYPES.DEFINITION_MODIFICATION],
    currentUtcDateValue,
  );
  const selfAssessmentTypePeriodActive = hasActivePeriod(
    periods,
    [PERIOD_TYPES.SELF_ASSESSMENT, PERIOD_TYPES.SELF_ASSESSMENT_MODIFICATION],
    currentUtcDateValue,
  );
  const selfAssessmentActionPeriodActive = hasActivePeriod(
    periods,
    [
      PERIOD_TYPES.DEFINITION,
      PERIOD_TYPES.DEFINITION_MODIFICATION,
      PERIOD_TYPES.SELF_ASSESSMENT,
      PERIOD_TYPES.SELF_ASSESSMENT_MODIFICATION,
    ],
    currentUtcDateValue,
  );
  const anyPlanningPeriodActive = selfAssessmentActionPeriodActive;

  return {
    definitionPeriodActive,
    definitionActionPeriodActive,
    selfAssessmentTypePeriodActive,
    selfAssessmentActionPeriodActive,
    anyPlanningPeriodActive,
    canReopenDefinitionNow: definitionActionPeriodActive,
    canReopenSelfAssessmentNow: selfAssessmentActionPeriodActive,
  };
};

export const getCreatePlanAvailability = ({
  userRoles,
  definitionPeriodActive,
  approvedRequest,
}: {
  userRoles: string[];
  definitionPeriodActive: boolean;
  approvedRequest?: ApprovedOutOfTimeRequestLike | null;
}): CreatePlanAvailability => {
  const isAdmin = userRoles.includes(AP_PERMISSIONS.ADMIN);
  const isSelfAssessment = userRoles.includes(AP_PERMISSIONS.SELF_ASSESSMENT);
  const approvedOutOfTimeRequestId = approvedRequest?.id ?? null;
  const hasApprovedOutOfTimeRequest =
    !!approvedOutOfTimeRequestId &&
    approvedRequest?.status === 'APPROVED' &&
    !approvedRequest?.usedAt;

  return {
    showCreatePlanButton: definitionPeriodActive && (isAdmin || isSelfAssessment),
    canCreateDelayedPlanAsAdmin: !definitionPeriodActive && isAdmin,
    canCreateDelayedPlanWithApproval:
      !definitionPeriodActive && !isAdmin && isSelfAssessment && hasApprovedOutOfTimeRequest,
    shouldShowRequestButton:
      !definitionPeriodActive && !isAdmin && isSelfAssessment && !hasApprovedOutOfTimeRequest,
    hasApprovedOutOfTimeRequest,
    isAdmin,
    isSelfAssessment,
  };
};

export const getPlanActionAvailability = ({
  plan,
  roles,
  periodState,
}: {
  plan:
    | Pick<
        Plan,
        | 'Status'
        | 'APVersionId'
        | 'APVersionState'
        | 'APFulfillmentsVersionId'
        | 'APFulfillmentsVersionState'
        | 'APVersionsCount'
        | 'APFulfillmentsVersionCount'
      >
    | null
    | undefined;
  roles: string[];
  periodState: AnnualPlanningPeriodState;
}): PlanActionAvailability => {
  if (!plan) {
    return getDisabledPlanActionAvailability();
  }

  const isAdmin = roles.includes(AP_PERMISSIONS.ADMIN);
  const isSelfAssessment = roles.includes(AP_PERMISSIONS.SELF_ASSESSMENT);
  const isCEO = roles.includes(AP_PERMISSIONS.CEO);
  const planVersionId = plan.APVersionId ?? null;
  const fulfillmentVersionId = plan.APFulfillmentsVersionId ?? null;
  const hasDefinitionVersion = planVersionId != null;
  const hasFulfillmentVersion = fulfillmentVersionId != null;
  const definitionState = plan.APVersionState;
  const fulfillmentState = plan.APFulfillmentsVersionState;
  const definitionApproved = definitionState === PLAN_VERSION_STATES.APPROVED;
  const definitionAwaiting = definitionState === PLAN_VERSION_STATES.AWAITING_APPROVAL;
  const definitionInProcess = definitionState === PLAN_VERSION_STATES.IN_PROCESS;
  const fulfillmentAwaiting = fulfillmentState === PLAN_VERSION_STATES.AWAITING_APPROVAL;
  const fulfillmentInProcess = fulfillmentState === PLAN_VERSION_STATES.IN_PROCESS;
  const definitionVersionsCount = plan.APVersionsCount ?? 0;
  const fulfillmentVersionsCount = plan.APFulfillmentsVersionCount ?? 0;
  const annulBlockedByVersionCounts =
    fulfillmentVersionsCount === 1 ||
    (definitionVersionsCount === 1 && fulfillmentVersionsCount === 0);
  const canEditDefinition =
    hasDefinitionVersion &&
    definitionInProcess &&
    periodState.definitionActionPeriodActive &&
    (isAdmin || isSelfAssessment);
  const canValidateDefinition =
    hasDefinitionVersion &&
    definitionAwaiting &&
    periodState.definitionActionPeriodActive &&
    (isAdmin || isCEO);
  const canHandleDefinition = canEditDefinition || canValidateDefinition;
  const canStartSelfAssessment =
    !hasFulfillmentVersion &&
    definitionApproved &&
    periodState.selfAssessmentActionPeriodActive &&
    (isAdmin || isSelfAssessment);
  const canEditSelfAssessment =
    hasFulfillmentVersion &&
    fulfillmentInProcess &&
    periodState.selfAssessmentActionPeriodActive &&
    (isAdmin || isSelfAssessment);
  const canValidateSelfAssessment =
    hasFulfillmentVersion &&
    fulfillmentAwaiting &&
    periodState.selfAssessmentActionPeriodActive &&
    (isAdmin || isCEO);
  const canHandleSelfAssessment =
    canStartSelfAssessment || canEditSelfAssessment || canValidateSelfAssessment;
  const canReopenDefinitionStage =
    (plan.Status === PLAN_STAGES.DEFINITION ||
      plan.Status === PLAN_STAGES.DEFINITION_OUT_OF_TIME) &&
    (definitionState === PLAN_VERSION_STATES.REJECTED ||
      definitionState === PLAN_VERSION_STATES.APPROVED) &&
    !!planVersionId &&
    periodState.canReopenDefinitionNow;
  const canReopenSelfStage =
    plan.Status === PLAN_STAGES.SELF_ASSESSMENT &&
    (fulfillmentState === PLAN_VERSION_STATES.REJECTED ||
      fulfillmentState === PLAN_VERSION_STATES.APPROVED) &&
    !!fulfillmentVersionId &&
    periodState.canReopenSelfAssessmentNow;
  const canAnnulPlan =
    (isAdmin || isSelfAssessment) &&
    periodState.anyPlanningPeriodActive &&
    !annulBlockedByVersionCounts;
  const canReturnToDefinition =
    isAdmin &&
    plan.Status === PLAN_STAGES.SELF_ASSESSMENT &&
    hasFulfillmentVersion &&
    fulfillmentVersionsCount === 1 &&
    periodState.anyPlanningPeriodActive;

  return {
    canEditDefinition,
    canValidateDefinition,
    canHandleDefinition,
    canStartSelfAssessment,
    canEditSelfAssessment,
    canValidateSelfAssessment,
    canHandleSelfAssessment,
    canReopenDefinitionStage,
    canReopenSelfStage,
    canShowReopen: canReopenDefinitionStage || canReopenSelfStage,
    canAnnulPlan,
    canReturnToDefinition,
  };
};
